let registrationPromise;
let updatePromise;
let pending = false;
let pageController;
const listeners = new Set();
const supported = () => typeof window !== 'undefined' && 'serviceWorker' in navigator && !navigator.userAgent.includes('ReactSnap');

function notify(value) {
  pending = value;
  listeners.forEach(listener => listener(value));
}

export function subscribeToUpdates(listener) {
  listeners.add(listener);
  listener(pending);
  return () => listeners.delete(listener);
}

function watch(reg) {
  pageController = navigator.serviceWorker.controller;
  const pageIsStale = () => Boolean(pageController && pageController !== navigator.serviceWorker.controller);
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    // Another tab may activate a release while this tab still runs its old bundle.
    notify(Boolean(reg.waiting) || pageIsStale());
  });
  const inspect = () => {
    notify(Boolean(reg.waiting) || pageIsStale());
    const worker = reg.installing;
    if (worker) worker.addEventListener('statechange', () => notify(Boolean(reg.waiting) || pageIsStale()));
  };
  reg.addEventListener('updatefound', inspect);
  inspect();
  const check = () => reg.update().catch(() => {});
  window.addEventListener('online', check);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check();
  });
  setInterval(() => {
    if (document.visibilityState === 'visible') check();
  }, 2 * 60 * 1000);
  return reg;
}

export function register() {
  if (!supported()) return Promise.resolve(null);
  if (!registrationPromise) {
    registrationPromise = navigator.serviceWorker.register('/service-worker.js', { updateViaCache: 'none' })
      .then(watch).catch(error => { registrationPromise = null; throw error; });
  }
  return registrationPromise;
}

function finishInstallation(reg) {
  const worker = reg.installing;
  if (!worker) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const done = () => {
      if (!['installed', 'activated', 'redundant'].includes(worker.state)) return;
      clearTimeout(timer);
      worker.removeEventListener('statechange', done);
      if (worker.state === 'redundant') reject(new Error('Update installation failed'));
      else resolve();
    };
    const timer = setTimeout(() => {
      worker.removeEventListener('statechange', done);
      reject(new Error('Update installation timed out'));
    }, 30000);
    worker.addEventListener('statechange', done);
    done();
  });
}

async function applyUpdate() {
  const reg = await register();
  if (!reg) return { unsupported: true, updated: false };
  await reg.update();
  await finishInstallation(reg);
  if (!reg.waiting) {
    if (pageController && pageController !== navigator.serviceWorker.controller) {
      window.location.reload();
      return { updated: true };
    }
    return { updated: false };
  }
  const worker = reg.waiting;
  await new Promise((resolve, reject) => {
    const container = navigator.serviceWorker;
    const changed = () => {
      if (container.controller !== worker) return;
      clearTimeout(timer);
      container.removeEventListener('controllerchange', changed);
      resolve();
    };
    const timer = setTimeout(() => {
      container.removeEventListener('controllerchange', changed);
      reject(new Error('Update activation timed out'));
    }, 30000);
    container.addEventListener('controllerchange', changed);
    worker.postMessage({ type: 'SKIP_WAITING' });
  });
  notify(false);
  window.location.reload();
  return { updated: true };
}

export function forceUpdate() {
  if (!updatePromise) updatePromise = applyUpdate().finally(() => { updatePromise = null; });
  return updatePromise;
}

export function getCurrentVersion() {
  const worker = supported() && navigator.serviceWorker.controller;
  if (!worker || typeof MessageChannel === 'undefined') return Promise.resolve(null);
  return new Promise(resolve => {
    const channel = new MessageChannel();
    const finish = value => { clearTimeout(timer); channel.port1.close(); channel.port2.close(); resolve(value); };
    const timer = setTimeout(() => finish(null), 3000);
    channel.port1.onmessage = event => finish(event.data);
    worker.postMessage({ type: 'GET_VERSION' }, [channel.port2]);
  });
}
